import { getDetailedErrorMessage } from "../../../utils/validation";
/**
 * Pulls the most useful line out of a seller-service failure.
 *
 * Requests reach the service through Apigee, which answers its own rejections
 * (quota, auth, routing) with `{ fault: { faultstring } }` rather than the
 * service's error body — `getDetailedErrorMessage` only reads the latter, so
 * without this an Apigee rejection would surface as a bare fallback message.
 */
const detailFrom = (apiError) => {
    const body = apiError?.response?.data;
    return body?.fault?.faultstring || body?.message || getDetailedErrorMessage(apiError, "");
};
/** Trace id off a failed response, which is when support most wants it. */
const requestIdFrom = (apiError) => apiError?.response?.headers?.["x-request-id"];
const withDetail = (baseMessage, apiError) => {
    const detail = detailFrom(apiError);
    return detail ? `${baseMessage} (${detail})` : baseMessage;
};
/** Error thrown when `GET /v1/advertiser/{id}` fails. */
export class AdvertiserProfileFetchFailed extends Error {
    constructor(apiError, baseMessage = "Failed to retrieve the advertiser profile. Please try again at a later time.") {
        super();
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AdvertiserProfileFetchFailed);
        }
        this.name = "AdvertiserProfileFetchFailed";
        this.message = withDetail(baseMessage, apiError);
    }
}
/** Error thrown when `PATCH /v1/advertiser/{id}` fails. */
export class AdvertiserProfileSaveFailed extends Error {
    constructor(apiError, baseMessage = "Failed to save the advertiser profile. Please try again at a later time.") {
        super();
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AdvertiserProfileSaveFailed);
        }
        this.name = "AdvertiserProfileSaveFailed";
        this.message = withDetail(baseMessage, apiError);
    }
}
/**
 * Error thrown when `POST /v1/advertiser:profile` fails.
 *
 * A timeout gets its own message: research runs against a 120s backend budget,
 * so a caller that hits the client bound is far more likely to succeed on a
 * retry than one that got a 4xx back.
 */
export class AdvertiserProfileResearchFailed extends Error {
    constructor(apiError, baseMessage = "Failed to research this advertiser. Please try again at a later time.") {
        super();
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AdvertiserProfileResearchFailed);
        }
        this.name = "AdvertiserProfileResearchFailed";
        this.requestId = requestIdFrom(apiError);
        this.message =
            apiError?.code === "ECONNABORTED"
                ? "Advertiser research timed out. Our AI provider may be busy — please try again."
                : withDetail(baseMessage, apiError);
    }
}
//# sourceMappingURL=errors.js.map